import { useState, useRef, useCallback, useId } from 'react';
import clsx from 'clsx';
import { Upload, X, ImageIcon, Film } from 'lucide-react';
import type { AdPlatform } from '@/shared/types/enums';
import {
  getConstraintsForPlatform,
  validateFile,
  formatFileSize,
  buildAcceptString,
  buildHintText,
} from './CreativeUploader.logic';
import styles from './CreativeUploader.module.scss';

/**
 * Represents a file that has been selected for upload.
 */
export interface UploadFileItem {
  /** Unique ID for keying */
  id: string;
  /** The original File object */
  file: File;
  /** Object URL for preview */
  previewUrl: string;
  /** Upload progress 0-100, or -1 for error */
  progress: number;
  /** Validation or upload error message */
  error?: string;
}

export interface CreativeUploaderProps {
  /** Currently tracked file items (controlled) */
  files: UploadFileItem[];
  /** Callback when files change (add/remove) */
  onFilesChange: (files: UploadFileItem[]) => void;
  /** Optional callback when a file is added, e.g. to trigger presigned URL upload */
  onFileAdded?: (item: UploadFileItem) => void;
  /** Selected ad platform for validation rules */
  platform?: AdPlatform;
  /** Allow multiple files */
  multiple?: boolean;
  /** Maximum number of files */
  maxFiles?: number;
  /** Disable the uploader */
  disabled?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Optional test id applied to the hidden file input (for e2e targeting). */
  inputTestId?: string;
}

let fileIdCounter = 0;
function generateFileId(): string {
  fileIdCounter += 1;
  return `upload-${fileIdCounter}-${Date.now()}`;
}

export function CreativeUploader({
  files,
  onFilesChange,
  onFileAdded,
  platform,
  multiple = true,
  maxFiles,
  disabled = false,
  className,
  inputTestId,
}: CreativeUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dropZoneId = useId();

  const constraints = getConstraintsForPlatform(platform);
  const acceptString = buildAcceptString(constraints);
  const hintText = buildHintText(constraints);

  const isAtMaxFiles = maxFiles != null && files.length >= maxFiles;

  // No unmount cleanup here on purpose. This component is CONTROLLED: the object
  // URLs live in the parent's state (the campaign wizard store) and outlive this
  // component, which unmounts on every wizard step change. Revoking them here
  // left the parent holding dead blob: strings, so the ad preview and the file
  // thumbnails broke on returning to the step. The owner revokes instead — see
  // `reset()` in campaign-wizard-store.ts — plus `handleRemove` below, which
  // drops the URL from parent state in the same call.

  const processFiles = useCallback(
    (rawFiles: FileList | File[]) => {
      const incoming = Array.from(rawFiles);
      if (incoming.length === 0) return;

      const slotsAvailable = maxFiles != null ? maxFiles - files.length : Infinity;
      const toProcess = incoming.slice(0, slotsAvailable);

      const newItems: UploadFileItem[] = toProcess.map((file) => {
        const validationError = validateFile(file, constraints);
        return {
          id: generateFileId(),
          file,
          previewUrl: URL.createObjectURL(file),
          progress: validationError ? -1 : 0,
          error: validationError?.reason,
        };
      });

      const updatedFiles = [...files, ...newItems];
      onFilesChange(updatedFiles);

      // Notify parent of each valid file so it can start upload
      newItems.forEach((item) => {
        if (!item.error && onFileAdded) {
          onFileAdded(item);
        }
      });
    },
    [files, constraints, maxFiles, onFilesChange, onFileAdded],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(e.target.files);
      }
      // Reset so the same file can be re-selected
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [processFiles],
  );

  const handleRemove = useCallback(
    (id: string) => {
      const item = files.find((f) => f.id === id);
      if (item?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.previewUrl);
      }
      onFilesChange(files.filter((f) => f.id !== id));
    },
    [files, onFilesChange],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isAtMaxFiles) {
        setIsDragOver(true);
      }
    },
    [disabled, isAtMaxFiles],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled || isAtMaxFiles) return;

      const { files: droppedFiles } = e.dataTransfer;
      if (droppedFiles.length > 0) {
        processFiles(droppedFiles);
      }
    },
    [disabled, isAtMaxFiles, processFiles],
  );

  const handleDropZoneClick = useCallback(() => {
    if (!disabled && !isAtMaxFiles) {
      inputRef.current?.click();
    }
  }, [disabled, isAtMaxFiles]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isAtMaxFiles) {
        e.preventDefault();
        inputRef.current?.click();
      }
    },
    [disabled, isAtMaxFiles],
  );

  const hasErrors = files.some((f) => f.error);

  return (
    <div className={clsx(styles.container, className)}>
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={disabled || isAtMaxFiles ? -1 : 0}
        aria-label="Upload files. Drag and drop or click to browse."
        aria-disabled={disabled || isAtMaxFiles}
        className={clsx(
          styles.dropZone,
          isDragOver && styles.dropZoneActive,
          disabled && styles.dropZoneDisabled,
          isAtMaxFiles && styles.dropZoneDisabled,
          hasErrors && styles.dropZoneError,
        )}
        onClick={handleDropZoneClick}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload size={24} className={styles.dropIcon} aria-hidden="true" />
        <span className={styles.dropText}>
          {isDragOver
            ? 'Drop files here'
            : isAtMaxFiles
              ? `Maximum ${maxFiles} files reached`
              : 'Click to upload or drag and drop'}
        </span>
        <span className={styles.dropHint}>{hintText}</span>
        <input
          ref={inputRef}
          id={dropZoneId}
          data-testid={inputTestId}
          type="file"
          accept={acceptString}
          multiple={multiple}
          disabled={disabled || isAtMaxFiles}
          onChange={handleInputChange}
          className={styles.fileInput}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {/* Platform-specific dimension hints */}
      {platform && constraints.recommendedImageDimensions.length > 0 && (
        <div className={styles.constraintsHint} aria-label="Recommended dimensions">
          <span>Recommended sizes:</span>
          {constraints.recommendedImageDimensions.map((dim) => (
            <span key={dim} className={styles.constraintTag}>
              {dim}
            </span>
          ))}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className={styles.fileList} role="list" aria-label="Uploaded files">
          {files.map((item) => (
            <FileItemRow key={item.id} item={item} onRemove={handleRemove} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- File item row ----

interface FileItemRowProps {
  item: UploadFileItem;
  onRemove: (id: string) => void;
}

function FileItemRow({ item, onRemove }: FileItemRowProps) {
  // `item.file` can be a non-File placeholder when a persisted wizard draft is
  // rehydrated — File objects don't survive a sessionStorage round-trip (they
  // serialize to `{}`). Guard every access so the row degrades gracefully
  // instead of crashing on `.type.startsWith`.
  const fileType = item.file?.type ?? '';
  const fileName = item.file?.name ?? 'Untitled file';
  const fileSize = item.file?.size ?? 0;
  const isImage = fileType.startsWith('image/');
  const isVideo = fileType.startsWith('video/');
  const isComplete = item.progress === 100;
  const isError = item.progress === -1 || !!item.error;

  return (
    <div
      className={clsx(styles.fileItem, isError && styles.fileItemError)}
      role="listitem"
      aria-label={fileName}
    >
      {/* Thumbnail */}
      <div className={styles.thumbnail}>
        {isImage && (
          <img
            src={item.previewUrl}
            alt={`Preview of ${fileName}`}
            className={styles.thumbnailImg}
          />
        )}
        {isVideo && (
          <video
            src={item.previewUrl}
            className={styles.thumbnailVideo}
            muted
            playsInline
            preload="metadata"
            aria-label={`Video preview of ${fileName}`}
          />
        )}
        {!isImage && !isVideo && (
          <span className={styles.thumbnailPlaceholder}>
            <ImageIcon size={24} aria-hidden="true" />
          </span>
        )}
      </div>

      {/* Info */}
      <div className={styles.fileInfo}>
        <span className={styles.fileName}>{fileName}</span>
        <span className={styles.fileMeta}>
          {isVideo && <Film size={12} aria-hidden="true" />} {formatFileSize(fileSize)}
        </span>
        {item.error && <span className={styles.fileError}>{item.error}</span>}

        {/* Progress bar */}
        {!isError && !isComplete && (
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={item.progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Upload progress for ${fileName}`}
          >
            <div className={styles.progressFill} style={{ width: `${item.progress}%` }} />
          </div>
        )}

        {isComplete && !isError && (
          <div className={styles.progressBar}>
            <div
              className={clsx(styles.progressFill, styles.progressFillComplete)}
              style={{ width: '100%' }}
            />
          </div>
        )}

        {isError && (
          <div className={styles.progressBar}>
            <div
              className={clsx(styles.progressFill, styles.progressFillError)}
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>

      {/* Remove */}
      <button
        type="button"
        className={styles.removeBtn}
        onClick={() => onRemove(item.id)}
        aria-label={`Remove ${fileName}`}
      >
        <X size={16} />
      </button>
    </div>
  );
}
